// React
import React, { useState } from "react";
import clsx from "clsx";

// MUI
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopTimePicker, LocalizationProvider, TimeView } from "@mui/x-date-pickers";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Paper, TextField } from "@mui/material";
import { GridSlots } from "@mui/x-data-grid";

// Third Party
import moment, { Moment } from "moment";

// Styles
import "./styles.css";

// Types
import { Nullish } from "@Common/interfaces/types";

type TimePickerProps = Readonly<{
	error: boolean;
	disabled: boolean;
	readOnly: boolean;
	toolbarFormat: string;
	clearable: boolean;
	allowSameDateSelection: boolean;
	disableHighlightToday: boolean;
	minTime: Moment;
	maxTime: Moment;
	minutesStep: number;
	showToolbar: boolean;
	disableOpenPicker: boolean;
	orientation: "portrait" | "landscape";
	toolbarTitle: string;
	mask: string;
	cancelText: string;
	okText: string;
	todayText: string;
	disableCloseOnSelect: boolean;
	views: TimeView[];
	placement: string;
	maxWidth: string;
	autocomplete: boolean;
	inputFormat: string;
	sx: object;
	defaultDate: Nullish<string>;
	onOpen: (date: Nullish<Moment | string>) => void;
	onActionUpdate: (date: Nullish<string>) => void;
	onAccept: (date: Nullish<string>) => void;
}>;

/********************************************** HELPERS COMPONENTS **********************************************/
const CustomTextField = (props: GridSlots["baseTextField"]) => <TextField {...props} />;

/********************************************** HELPERS FUNCTIONS **********************************************/
const theme = createTheme({
	palette: {
		primary: {
			main: "#019bd9",
		},
	},
});

const getMomentDate = (date: Nullish<Moment | string>) => {
	if (!date) {
		return null;
	}
	if (date instanceof moment) {
		return date;
	}
	return moment(date);
};

const getStringDate = (date: Nullish<Moment | string>) => {
	if (!date) {
		return null;
	}
	return moment(date).toISOString();
};

export default function TimePicker(properties: TimePickerProps) {
	const { defaultDate = "", onAccept = () => {}, onActionUpdate = () => {}, ...props } = properties;

	// Local State
	const [value, setValue] = useState<Nullish<Moment | string>>(defaultDate);
	const [open, setOpen] = useState(false);

	/********************************************** HANDLERS **********************************************/
	const handleChange = (date: Nullish<Moment>) => {
		if (date) {
			onActionUpdate(getStringDate(date));
		}
	};

	const handleOnClose = () => {
		setOpen(false);
	};

	const handleAccept = (date: Nullish<Moment>) => {
		setValue(date);
		if (date) {
			onActionUpdate(getStringDate(date));
		} else {
			onAccept?.(getStringDate(value));
		}
	};

	React.useEffect(() => {
		let date = null;
		if (defaultDate) {
			date = moment(defaultDate);
		}
		setValue(date);

		return () => {
			setValue(null);
		};
	}, [defaultDate]);

	const momentValue = getMomentDate(value);

	return (
		<ThemeProvider theme={theme}>
			<Paper
				elevation={0}
				className={"date-time-picker-container"}
				style={{ position: "relative", backgroundColor: "transparent", width: "100%" }}
				sx={props.sx}
			>
				<LocalizationProvider dateAdapter={AdapterMoment}>
					<DesktopTimePicker
						{...props}
						label=""
						open={open}
						value={momentValue}
						format={props.inputFormat}
						className={clsx({
							"text-input-error-date-picker": props.error,
						})}
						views={props?.views || ["hours", "minutes"]}
						slots={{
							textField: CustomTextField,
						}}
						slotProps={{
							layout: {
								sx: {
									".MuiMultiSectionDigitalClockSection-root": {
										position: "relative",
										height: "100%",
										border: "1px solid #CCCCCC",
									},
									".MuiMultiSectionDigitalClockSection-item": {
										fontWeight: 400,
										fontSize: "14.5px",
										marginY: "4px",
									},
									".MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar": {
										display: "block",
										width: "7px",
										borderRight: "4px solid transparent",
									},
									".MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-track": {
										background: "transparent",
										display: "block",
										margin: "3px",
									},
									".MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-thumb": {
										borderRight: "none",
										borderLeft: "none",
									},
									".MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-track-piece:end": {
										background: "transparent",
									},
									".MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-track-piece:start": {
										background: "transparent",
									},
								},
							},
							popper: {
								sx: {
									".MuiMultiSectionDigitalClockSection-item": {
										fontFamily: "PTSansProNarrow",
										color: "#666666",
										"&:hover": {
											backgroundColor: "rgba(35, 87, 190, 0.25)",
											borderRadius: "6px",
											color: "#666666",
										},
										"&.Mui-selected": {
											backgroundColor: "#2357BE !important",
											borderRadius: "6px",
											color: "white",
										},
									},
								},
							},
							actionBar: {
								sx: {
									height: "32px",
									".MuiButton-root": {
										color: "#2357BE",
										fontFamily: "Montserrat",
										marginRight: "-10px",
										fontSize: "13px",
									},
								},
							},
							openPickerButton: {
								sx: {
									paddingX: "12px",
									"& .MuiSvgIcon-root": {
										fontSize: "18px",
									},
									color: "#666666 !important",
								},
							},
							textField: {
								sx: {
									"& .MuiOutlinedInput-root": {
										"& fieldset": {
											borderColor: "#CCCCCC !important",
										},
									},
								},
								fullWidth: true,
								disabled: props.disabled,
								onClick: () => {
									if (!props.disabled) {
										setOpen(true);
									}
									props.onOpen?.(value);
								},
								className: clsx({
									"date-time-picker-input correct-shink-label": true,
								}),
								autoComplete: !props?.autocomplete ? "off" : "on",
							},
						}}
						onOpen={() => setOpen(true)}
						onChange={handleChange}
						onClose={handleOnClose}
						onAccept={handleAccept}
					/>
				</LocalizationProvider>
			</Paper>
		</ThemeProvider>
	);
}
